<script setup lang="ts">
import {
  ref,
  computed,
  provide,
  watch,
  inject,
  onMounted,
  defineEmits
} from 'vue';
import { inputFocusKey, configuratorStateKey } from './ProductConfiguratorKeys';
import { changeImageKey } from '../ProductOverview/ProductOverviewKeys';
import { setItemKey, routeKey } from '@drapejs/core';
import { useCartFunctions } from '@/helpers/cart-actions';
import useContext from '@/composables/useContext';
import * as config from '@/config';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import ConfiguratorSummary from './ProductSummary/ConfiguratorSummary.vue';
import ConfiguratorQuantitySelector from './ProductQuantity/ConfiguratorQuantitySelector.vue';
import ConfiguratorPriceBrackets from './ProductPrice/ConfiguratorPriceBrackets.vue';
import ConfiguratorProductDecorator from './ProductDecorations/ConfiguratorProductDecorator.vue';
import ConfiguratorChevron from './ConfiguratorChevron.vue';
import ModalContainer from '@/components/ModalContainer.vue';
import GiftcardTypeSelector from '@/components/Giftcard/GiftcardTypeSelector.vue';
import ProductInformation from './ProductInformation/ProductInformation.vue';
import GiftcardCountrySelector from '../Giftcard/GiftcardCountrySelector.vue';
import GiftcardDeliverytypeSelector from '../Giftcard/GiftcardDeliverytypeSelector.vue';
import GiftcardDesignAddon from '../Giftcard/Addons/GiftcardDesignAddon.vue';
import GiftcardGreetingAddon from '../Giftcard/Addons/GiftcardGreetingAddon.vue';
import GiftcardBoxAddon from '../Giftcard/Addons/GiftcardBoxAddon.vue';
import ErrorMessage from '../forms/ErrorMessage.vue';

const { isAuthorized, navigateToLoginPage, channel, page, cart, user } =
  useContext();
const { websiteText } = useWebsiteTexts();
const { addItemToCart } = useCartFunctions();
const registerModal = inject('registerModal');
const unregisterModal = inject('unregisterModal');
const route = inject(routeKey);
const setItem = inject(setItemKey);
const updateImagesFilter = inject('changeImageKey');
const emit = defineEmits(['removeGroup']);
const isClientRender = typeof window !== 'undefined';

interface ConfiguratorState {
  selections: ConfiguratorSelections;
  decorationSteps: ConfiguratorDecorationSteps;
  quantities: VariantQuantities;
  sumQuantity: 0;
  activePriceBracket: PriceBracket;
  supportedVariants: [];
  desiredWidth: 0;
  desiredHeight: 0;
  customPrice: 0;
  recipientCountry: RecipientCountry;
  giftcardType: string;
}

interface ConfiguratorSelections {
  selectedPlacement: string;
  selectedPlacementName: string;
  selectedTechnique: string;
  selectedTechniqueVariant: string;
  selectedTechniqueVariantPrices: [];
  selectedTechniqueVariantAddons: [];
  selectedTechniqueVariantInputColors: {};
  selectedAddons: [];
  fileUpload: FileUpload | null;
  desiredWidth: 0;
  desiredHeight: 0;
}

interface ConfiguratorDecorationSteps {
  initialState: boolean;
  placeDecoration: boolean;
  pickTechnique: boolean;
  uploadDecoration: boolean;
  confirmDecoration: boolean;
  decorationSummary: boolean;
}

interface VariantQuantities {
  variantSystemId: string;
  quantity: number;
}

interface FileUpload {
  Name: string;
  SystemId: string;
  ThumbnailUrl: string;
  Url: string;
}

interface PriceBracket {
  name: string;
  price: number;
  decorationPrice: number;
}

interface RecipientCountry {
  id: string;
  name: string;
}

const props = defineProps({
  configuratorSavedState: {
    type: Object,
    default: undefined
  },
  decorations: {
    type: Array,
    required: true
  },
  addons: {
    type: Array,
    required: true
  },
  variants: {
    type: Array,
    required: true
  },
  modal: {
    type: Boolean,
    default: false
  },
  baseProductName: {
    type: String,
    default: ''
  },
  baseProductSystemId: {
    type: String,
    default: ''
  },
  selectedVariant: {
    type: Object,
    default: {}
  },
  isGiftCard: {
    type: Boolean,
    default: false
  },
  giftCardFreightPrice: {
    type: Number,
    default: 0
  }
});

const productsEdited = ref(false);
const inputFocus = ref(false);
const isDecorating = ref(false);
const unfinishedDecorationModalOpen = ref(false);
const description = computed(() => props.selectedVariant?.fields?._description);
const hasDecoration = computed(
  () => !!configuratorState.value.selections.selectedTechniqueVariant
);
const hasAddons = computed(
  () => configuratorState.value.selections.selectedAddons.length > 0
);
const firstVariant = computed(() =>
  props.variants?.length > 0 ? props.variants[0] : {}
);
const canAddToCart = computed(() => configuratorState.value.sumQuantity > 0);
const decorationsExist = computed(() => !!props.decorations.length);
const designAddonComponent = ref(null);
const greetingAddonComponent = ref(null);
const boxAddonComponent = ref(null);
const typeSelectorComponent = ref(null);
const descriptionReadMoreEnabled = computed(
  () => description?.value?.length > 500
);
const descriptionReadMore = ref(false);
const isFlexible = computed(
  () => props.variants.filter((v) => v.fields.IsFlexibleType).length > 0
);

function initialVariantQuantity(): VariantQuantities {
  return props.variants.reduce((acc, v) => {
    acc[v.systemId] = 0;
    return acc;
  }, {});
}

function initialActivePriceBrackets(): PriceBracket {
  return props.variants.reduce((acc, v) => {
    acc[v.systemId] = {};
    return acc;
  }, {});
}

function initialCustomPrice() {
  return (
    props.selectedVariant?.price ??
    props.selectedVariant?.fields?.MinGiftCardPrice ??
    0
  ).toString();
}

const outOfPrintStock = computed(() => {
  const totalPrintStock = props.variants.reduce((acc, variant) => {
    const currentStock = variant.printableStock?.currentStock?.amount;
    const incomingStock = variant.printableStock?.incomingStock;
    if (currentStock) {
      acc += currentStock;
    }

    if (incomingStock?.length) {
      incomingStock.forEach((i) => {
        if (i.amount) {
          acc += i.amount;
        }
      });
    }

    return acc;
  }, 0);

  return totalPrintStock === 0;
});

const addonMinimumQuantityFullfilled = computed(() => {
  return configuratorState.value.selections.selectedAddons.every(
    (a) =>
      a.variant.minimumQuantity <= configuratorState.value.sumQuantity ||
      a.variant.minimumQuantity == 0
  );
});

const variantMaximumQuantityFullfilled = computed(() => {
  const maxAmount = props.selectedVariant.fields.MaxGiftCardAmount;
  return !maxAmount || configuratorState.value.sumQuantity <= maxAmount;
});

const orderRequirementsFullfilled = computed(() => {
  return (
    addonMinimumQuantityFullfilled.value &&
    variantMaximumQuantityFullfilled.value
  );
});

function getDefaultConfiguratorState() {
  return {
    selections: {
      selectedPlacement: '',
      selectedPlacementName: '',
      selectedTechnique: '',
      selectedTechniqueVariant: '',
      selectedTechniqueVariantPrices: [],
      selectedTechniqueVariantAddons: [],
      selectedTechniqueVariantInputColors: {},
      selectedAddons: [],
      fileUpload: null,
      desiredWidth: 0,
      desiredHeight: 0
    },
    decorationSteps: {
      initialState: true,
      placeDecoration: false,
      pickTechnique: false,
      uploadDecoration: false,
      confirmDecoration: false,
      decorationSummary: false
    },
    quantities: initialVariantQuantity(),
    sumQuantity: 0,
    activePriceBracket: initialActivePriceBrackets(),
    supportedVariants: [],
    desiredWidth: 0,
    desiredHeight: 0,
    customPrice: initialCustomPrice(),
    recipientCountry: {},
    giftcardType: 'Physical'
  };
}

const configuratorState = ref<ConfiguratorState>(
  props.configuratorSavedState || getDefaultConfiguratorState()
);

watch(
  () => props.baseProductSystemId,
  () => {
    configuratorState.value = getDefaultConfiguratorState();
    isDecorating.value = false;
  }
);

watch(
  () => page.value,
  () => {
    refreshAllPrices();
  }
);

watch(
  () => props.variants,
  () => {
    refreshAllPrices();
  }
);

function recalculateDecorationPrices(decorations?: Array) {
  if (
    !decorations?.length ||
    !configuratorState.value.selections.selectedTechniqueVariant
  )
    return;

  const selection = configuratorState.value.selections;
  const currentTechiqueVariant = decorations
    .find((d) => d.systemId === selection.selectedPlacement)
    .techniques.find((t) => t.systemId === selection.selectedTechnique)
    .variants.find((v) => v.systemId === selection.selectedTechniqueVariant);

  if (!currentTechiqueVariant) return;

  selection.selectedTechniqueVariantPrices.forEach(
    (p, idx) => (p.price = currentTechiqueVariant.prices[idx].price)
  );

  selection.selectedTechniqueVariantAddons.forEach(
    (a, idx) => (a.price = currentTechiqueVariant.addons[idx].price)
  );
}

function updateFocusState(val: boolean) {
  inputFocus.value = val;
}

function updateSelection(fieldName: string, val: any) {
  configuratorState.value.selections[fieldName] = val;
}

function updateDecorationSteps(fieldName: string, val: boolean) {
  configuratorState.value.decorationSteps[fieldName] = val;
}

function updateQuantity(variantId: string, val: number) {
  configuratorState.value.quantities[variantId] = val;
  configuratorState.value.sumQuantity = getVariantsToShow().reduce(
    (acc, curr) => acc + configuratorState.value.quantities[curr.systemId],
    0
  );
  updateActivePriceBrackets();
}

function updateCustomPrice(val: number) {
  configuratorState.value.customPrice = val;
}

function removeUpload() {
  if (
    !channel.value?.systemId ||
    !configuratorState.value.selections.fileUpload?.systemId
  )
    return;

  const requestContext = {
    channelSystemId: channel.value.systemId,
    websiteSystemId: channel.value.website?.systemId,
    culture: channel.value.locale
  };

  fetch(
    `${config.litiumBaseUrl}/api/productprint?fileSystemId=${configuratorState.value.selections.fileUpload.systemId}`,
    {
      method: 'DELETE',
      headers: {
        'litium-request-context': JSON.stringify(requestContext)
      }
    }
  )
    .then((response) => {
      if (!response.ok) {
        throw Error(response.statusText);
      }

      return response.json();
    })
    .catch((error) => {
      console.log(error);
    });
}

function removeDecoration() {
  const selection = configuratorState.value.selections;
  selection.selectedPlacement = '';
  selection.selectedPlacementName = '';
  selection.selectedTechnique = '';
  selection.selectedTechniqueVariant = '';
  selection.selectedTechniqueVariantPrices = [];
  selection.selectedTechniqueVariantAddons = [];
  selection.selectedTechniqueVariantInputColors = {};
  selection.desiredWidth = 0;
  selection.desiredHeight = 0;
  selection.fileUpload = null;
  isDecorating.value = false;
}

function getCurrentPriceBracket(amount: number, brackets: any) {
  const bracket = brackets?.reduce(
    (curr, b) => {
      return b.bracket > curr.bracket && b.bracket <= amount ? b : curr;
    },
    { bracket: 0, price: 0 }
  );
  return bracket ?? {};
}

function updateActivePriceBrackets() {
  const activeVariants = props.variants.filter(
    (v) => configuratorState.value.quantities[v.systemId] > 0
  );
  activeVariants.forEach((variant) => {
    const priceBracket = getCurrentPriceBracket(
      configuratorState.value.sumQuantity,
      variant.prices
    );
    configuratorState.value.activePriceBracket[variant.systemId] = priceBracket;
  });
}

async function checkDecorationProgressAndAddToCart() {
  if (
    configuratorState.value.decorationSteps.decorationSummary ||
    !isDecorating.value
  ) {
    addToCart();
  } else {
    openConfirmationModal();
  }
}

function goToStep(step: string) {
  Object.keys(configuratorState.value.decorationSteps).forEach((k) => {
    updateDecorationSteps(k, k === step);
  });
}

function openConfirmationModal() {
  registerModal('UnfinishedDecoration');
  unfinishedDecorationModalOpen.value = true;
}

function closeConfirmationModal() {
  unfinishedDecorationModalOpen.value = false;
  unregisterModal('UnfinishedDecoration');
}

function cancelConfirmation() {
  removeUpload();
  removeDecoration();
  goToStep('initialState');
  closeConfirmationModal();
  configuratorState.value.supportedVariants = [];
}

function navigateToLogin() {
  const search = route?.query || {};
  const query = {
    ...search,
    progress: 'product-configuration'
  };
  sessionStorage.setItem(
    'product-configuration',
    JSON.stringify(configuratorState.value)
  );
  navigateToLoginPage(query);
}

async function addToCart() {
  if (!isAuthorized()) {
    navigateToLogin();
    return;
  }
  
  const variantsToAdd = Object.entries(configuratorState.value.quantities)
    .filter(([k, v]) => v)
    .map(([k, v]) => {
      const articleNumber = props.variants.find(v => v.systemId == k)?.articleNumber;
      
      return { VariantSystemId: k, Quantity: v, ArticleNumber:  articleNumber}});
  const variantData = {
    articleNumber: '',
    variantSystemId: '',
    variants: JSON.stringify(variantsToAdd),
    quantity: 0,
    giftCardInfo: {}
  };

  if (props.isGiftCard) {
    const giftCardInfo = {
      RecipientCountry: configuratorState.value.recipientCountry.id,
      GiftCardType: configuratorState.value.giftcardType,
      Addons: configuratorState.value.selections?.selectedAddons?.map(
        (addon) => {
          return {
            id: addon.variant.id,
            name: addon.variant.name,
            systemId: addon.variant.systemId,
            price: addon.variant.prices[0]?.price || 0,
            quantity: addon.variant.ignoreGiftCardQuantity
              ? 1
              : configuratorState.value.sumQuantity,
            extraInfo: addon.variant.extraInfo
          };
        }
      ),
      GiftCardShipping: {
        id: 'PKFRAKT',
        quantity: configuratorState.value.sumQuantity,
        price: props.giftCardFreightPrice
      },
      CustomPrice: configuratorState.value.customPrice,
      IsFlexible: isFlexible.value
    };
    variantData.giftCardInfo = giftCardInfo;
  } else {
    variantData.giftCardInfo = {};
  }

  const selections = configuratorState.value.selections;
  const selectedPrintData = {
    printVariantSystemId: selections.selectedTechniqueVariant,
    printProductSystemId: selections.selectedTechnique,
    printImageSystemId: selections.fileUpload?.systemId || '',
    printAddons: selections.selectedTechniqueVariantAddons,
    printPosition: selections.selectedPlacementName,
    printPreviewDataUrl: '',
    printText: '',
    desiredWidth: selections.desiredWidth,
    desiredHeight: selections.desiredHeight,
    colors: JSON.stringify(selections.selectedTechniqueVariantInputColors || {})
  };

  const { row, error } = await addItemToCart(variantData, selectedPrintData);

  if (error !== 'NONE') {
    if (error === 'NOT_ENOUGH_STOCK') {
      console.log('NOT_ENOUGH_STOCK');
    } else if (error === 'NOT_AUTHENTICATED') {
      console.log('NOT_AUTHENTICATED');
    } else {
      console.log(error);
    }
    return;
  }

  setItem(
    `__configurator_${row.rowGroupId}`,
    JSON.stringify(configuratorState.value)
  );

  configuratorState.value.quantities = initialVariantQuantity();
  configuratorState.value.sumQuantity = 0;
  configuratorState.value.activePriceBracket = initialActivePriceBrackets();

  Object.keys(configuratorState.value.decorationSteps).forEach((k) => {
    updateDecorationSteps(k, k === 'initialState');
  });

  resetFields();
  removeDecoration();
}

function toggleReadMoreDescription() {
  descriptionReadMore.value = !descriptionReadMore.value;
}

function begunDecoration() {
  isDecorating.value = true;
}

async function checkDecorationProgressAndEditCart() {
  if (
    configuratorState.value.decorationSteps.decorationSummary ||
    !isDecorating.value
  ) {
    emit('removeGroup');
    addToCart();
  } else {
    openConfirmationModal();
  }
}

function editProductsInCartGroup() {
  if (!isAuthorized()) {
    navigateToLogin();
    return;
  }
  checkDecorationProgressAndEditCart();
}

function techniqueVariantSelected(variant) {
  configuratorState.value.supportedVariants = variant.supportedVariants;
}

function getVariantsToShow() {
  if (!props.isGiftCard) return props.variants;
  if (props.selectedVariant !== undefined) return [props.selectedVariant];
  return [];
}

function changeRecipientCountry(country) {
  configuratorState.value.recipientCountry = country;
}

function changeGiftcardType(type) {
  configuratorState.value.giftcardType = type;
}

function addAddonSelection(addon: string, variant: string, extraInfo: string) {
  const propsAddon = props.addons.filter((a) => a.id === addon)[0];
  const addonVariant = propsAddon.variants.filter((v) => v.id === variant)[0];
  updateImagesFilter(variant);
  removeAddonSelection(addon);
  configuratorState.value.selections.selectedAddons.push({
    id: propsAddon.id,
    name: propsAddon.name,
    systemId: propsAddon.systemId,
    variant: {
      ...addonVariant,
      extraInfo
    }
  });
}

function removeAddonSelection(addon: string) {
  configuratorState.value.selections.selectedAddons =
    configuratorState.value.selections.selectedAddons.filter(
      (a) => a.id !== addon
    );
}

function refreshAllPrices() {
  configuratorState.value.selections.selectedAddons.forEach((a) => {
    addAddonSelection(a.id, a.variant.id, null);
  });
  updateActivePriceBrackets();
  recalculateDecorationPrices(page.value?.dataJson?.dataJson?.product?.fields?.Decorations);
}

watch(
  () => props.selectedVariant,
  (next, curr) => {
    if (next.articleNumber !== curr.articleNumber) {
      updateQuantity(curr.systemId, 0);
    }
  }
);

watch(
  configuratorState,
  () => {
    if (!productsEdited.value) {
      productsEdited.value = true;
    }
  },
  { deep: true, immediate: true }
);

onMounted(() => {
  const savedState = sessionStorage.getItem('product-configuration');
  sessionStorage.removeItem('product-configuration');
  if (savedState) {
    configuratorState.value = { ...JSON.parse(savedState) };

    if (!configuratorState.value.decorationSteps.initialState) {
      isDecorating.value = true;
    }
  }
  refreshAllPrices();
});

const selectedDecorationName = computed(() => {
  const selections = configuratorState.value.selections;

  const selectedPlacement = props.decorations.find(
    (d) => d.position.value === selections.selectedPlacementName
  );
  if (!selectedPlacement) {
    return '';
  }

  const selectedTechnique = selectedPlacement.techniques.find(
    (t) => t.systemId === selections.selectedTechnique
  );
  if (!selectedTechnique) {
    return '';
  }

  if (selectedTechnique.variants.length == 1) {
    return selectedTechnique.name;
  }

  const selectedVariant = selectedTechnique.variants.find(
    (v) => v.systemId === selections.selectedTechniqueVariant
  );
  if (!selectedVariant) {
    return '';
  }

  return selectedVariant.name;
});

function displayDesignAddon() {
  return !!designAddon() && designAddon().variants.length > 0;
}

function displayGreetingAddon() {
  return !!greetingAddon() && greetingAddon().variants.length > 0;
}

function displayBoxAddon() {
  return !!boxAddon() && boxAddon().variants.length > 0;
}

const designAddon = () => {
  return props.addons.filter((addon) => addon.id === 'Addon-Design')[0];
};

const greetingAddon = () => {
  return props.addons.filter((addon) => addon.id === 'Addon-Greeting')[0];
};

const boxAddon = () => {
  return props.addons.filter((addon) => addon.id === 'Addon-Box')[0];
};

function resetFields() {
  designAddonComponent.value?.resetAddon();
  greetingAddonComponent.value?.resetAddon();
  boxAddonComponent.value?.resetAddon();
  typeSelectorComponent.value?.resetValues();
}

provide(inputFocusKey, { inputFocus, updateFocusState });
provide(configuratorStateKey, {
  configuratorState,
  updateSelection,
  updateDecorationSteps,
  updateQuantity,
  removeDecoration,
  updateActivePriceBrackets,
  removeUpload,
  updateCustomPrice
});
</script>
<template>
  <div class="product-config">
    <div v-if="modal" class="product-config__modal-options">
      <div class="product-config__modal-title">
        {{
          websiteText('configurator__edit_product', {
            product: baseProductName
          }).value
        }}
      </div>
      <button
        :disabled="!productsEdited"
        class="product-config__modal-save primary-button"
        @click="editProductsInCartGroup()"
      >
        {{ websiteText('giftcard__save_changes').value }}
        <ConfiguratorChevron
          :open="true"
          class="product-config__modal-save-chevron"
        >
        </ConfiguratorChevron>
      </button>
    </div>
    <div class="product-config-components">
      <div class="product-config__main">
        <div
          v-if="!props.isGiftCard"
          class="product-config__modal-description"
          :class="
            descriptionReadMore ||
            !descriptionReadMoreEnabled ||
            'product-config__modal-description-short'
          "
        >
          {{ description }}
        </div>
        <button
          class="product-config__modal-description-read-more-button"
          @click="toggleReadMoreDescription()"
          v-if="!props.isGiftCard && descriptionReadMoreEnabled"
        >
          {{
            descriptionReadMore
              ? websiteText('global__read_less').value
              : websiteText('global__read_more').value
          }}
        </button>
        <GiftcardTypeSelector
          v-if="isGiftCard"
          :isGiftCard="isGiftCard"
          :variants="variants"
          :giftCardFreightPrice="props.giftCardFreightPrice"
          :current="props.selectedVariant"
          ref="typeSelectorComponent"
        ></GiftcardTypeSelector>
        <ConfiguratorQuantitySelector
          :variants="getVariantsToShow()"
          :supportedVariants="configuratorState.supportedVariants"
          :selectedDecorationName="selectedDecorationName"
          :has-decoration="hasDecoration"
          :isGiftCard="isGiftCard"
        ></ConfiguratorQuantitySelector>
        <ConfiguratorPriceBrackets
          v-if="!isGiftCard"
          :product="props.selectedVariant"
          :has-decoration="decorationsExist"
        ></ConfiguratorPriceBrackets>
        <ConfiguratorProductDecorator
          v-if="decorationsExist"
          :out-of-stock="outOfPrintStock"
          :variants="variants"
          :decorations="decorations"
          @started="begunDecoration"
          @techniqueVariantSelected="techniqueVariantSelected"
        ></ConfiguratorProductDecorator>
        <GiftcardDesignAddon
          v-if="isClientRender && isGiftCard && displayDesignAddon()"
          :current="props.selectedVariant"
          ref="designAddonComponent"
          :addon="designAddon()"
          @addAddonSelection="addAddonSelection"
          @removeAddonSelection="removeAddonSelection"
          @navigateToLogin="navigateToLogin"
        ></GiftcardDesignAddon>
        <GiftcardGreetingAddon
          v-if="isClientRender && isGiftCard && displayGreetingAddon()"
          ref="greetingAddonComponent"
          :addon="greetingAddon()"
          @addAddonSelection="addAddonSelection"
          @removeAddonSelection="removeAddonSelection"
        ></GiftcardGreetingAddon>
        <GiftcardBoxAddon
          v-if="isClientRender && isGiftCard && displayBoxAddon()"
          :addon="boxAddon()"
          ref="boxAddonComponent"
          @addAddonSelection="addAddonSelection"
          @removeAddonSelection="removeAddonSelection"
        ></GiftcardBoxAddon>
        <GiftcardCountrySelector
          v-if="isClientRender && isGiftCard"
          :variants="variants"
          @changeRecipientCountry="changeRecipientCountry"
        ></GiftcardCountrySelector>
        <GiftcardDeliverytypeSelector
          v-if="isClientRender && isGiftCard"
          :variants="variants"
          @changeGiftcardType="changeGiftcardType"
        ></GiftcardDeliverytypeSelector>
      </div>
      <div class="product-config__summary">
        <ConfiguratorSummary
          :can-add-to-cart="canAddToCart"
          :has-decoration="hasDecoration"
          :has-addons="hasAddons"
          :variants="getVariantsToShow()"
          :decorations="decorations"
          :isGiftCard="isGiftCard"
          :giftCardFreightPrice="props.giftCardFreightPrice"
        >
        </ConfiguratorSummary>
        <ErrorMessage
          v-if="!addonMinimumQuantityFullfilled"
          :message="websiteText('giftcard__addon_summary_quantity_error').value"
        />
        <ErrorMessage
          v-if="!variantMaximumQuantityFullfilled"
          :message="
            websiteText('giftcard__addon_summary_too_many_error', {
              amount: selectedVariant.fields.MaxGiftCardAmount
            }).value
          "
        />
        <button
          v-if="!modal"
          class="product-config__add-to-cart primary-button"
          @click="checkDecorationProgressAndAddToCart()"
          :disabled="!canAddToCart || !orderRequirementsFullfilled"
        >
          {{ websiteText('global__add_to_cart').value }}
        </button>
        <button
          v-if="modal"
          :disabled="!productsEdited"
          class="product-config__modal-save-checkout primary-button"
          @click="editProductsInCartGroup()"
        >
          {{ websiteText('giftcard__save_changes').value }}
          <ConfiguratorChevron
            :open="true"
            class="product-config__modal-save-checkout-chevron"
          >
          </ConfiguratorChevron>
        </button>
        <ProductInformation :selectedVariant="selectedVariant">
        </ProductInformation>
      </div>
    </div>
    <ModalContainer
      v-if="unfinishedDecorationModalOpen"
      :show-exit-button="true"
      position="center"
      @close-modal="closeConfirmationModal"
    >
      <div class="unfinished-decoration__modal">
        <p class="unfinished-decoration__modal-title">
          {{ websiteText('decoration__unfinished_title').value }}
        </p>
        <div class="unfinished-decoration__modal-options">
          <button
            class="unfinished-decoration__modal-cancel secondary-button"
            @click="cancelConfirmation"
          >
            {{ websiteText('decoration__unfinished_cancel').value }}
          </button>
          <button
            class="unfinished-decoration__modal-accept primary-button"
            @click="closeConfirmationModal"
          >
            {{ websiteText('decoration__unfinished_accept').value }}
          </button>
        </div>
      </div>
    </ModalContainer>
  </div>
</template>

<style>
.modal-container .product-config {
  height: 100%;
}

.product-config-components {
  display: grid;
  gap: 10px;
  max-width: 648px;
}

.modal-container--right .product-config-components {
  padding: 15px 20px 20px;
  max-height: calc(100% - 93px);
  overflow-y: auto;
  position: relative;
}

.modal-container--center .product-config-components {
  padding: 15px 20px 25px;
  max-width: none;
  max-height: calc(100% - 48px);
  overflow-y: auto;
}

.product-config__modal-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--color-white);
  padding: 43px 20px 10px;
  box-shadow: 4px 5px 20px rgb(0 81 121 / 7%);
}

.product-config__modal-description {
  font-size: 14px;
  line-height: 150%;
}

.product-config__modal-description-short {
  max-height: 120px;
  overflow: hidden;
  --mask: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0,
      rgba(0, 0, 0, 1) 50%,
      rgba(0, 0, 0, 0) 100%,
      rgba(0, 0, 0, 0) 0
    )
    100% 50% / 100% 100% repeat-x;
  mask: var(--mask);
}

.product-config__modal-description-read-more-button {
  display: block;
  justify-self: right;
  font-size: 14px;
}

.modal-container--center .product-config__modal-options {
  padding: 14px 20px 14px;
}

.product-config__modal-title {
  font-size: 16px;
  line-height: 20px;
  font-style: italic;
  color: var(--color-neutrals-80);
  margin-right: 10px;
}

.product-config__modal-save:disabled {
  cursor: default;
}

.modal-container--center .product-config__modal-save {
  display: none;
}

.product-config__main {
  display: grid;
  gap: 10px;
  align-content: flex-start;
}

.modal-container--center .product-config__main {
  height: 100%;
}

.product-config__summary {
  display: grid;
  gap: 10px;
}

button.product-config__modal-save,
button.product-config__modal-save-checkout {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 12px;
  padding-left: 25px;
  white-space: nowrap;
}

button.product-config__modal-save {
  padding-right: 15px;
}

.modal-container--right .product-config__modal-save-checkout {
  display: none;
}

.product-config__modal-save-chevron,
.product-config__modal-save-checkout-chevron {
  transform: rotate(-90deg);
  flex-shrink: 0;
}

.product-config__modal-save-chevron {
  margin-left: 10px;
}

.unfinished-decoration__modal {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px;
  position: relative;
}

.unfinished-decoration__modal-title {
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  text-align: center;
  margin: 0 0 15px;
}

.unfinished-decoration__modal-subtitle {
  font-size: 14px;
  line-height: 24px;
  text-align: center;
  margin: 0 0 20px;
}

.unfinished-decoration__modal-options {
  display: grid;
  gap: 10px;
  width: 100%;
  padding: 0 20%;
}

button.unfinished-decoration__modal-cancel,
button.unfinished-decoration__modal-accept {
  padding: 0 15px;
  width: 100%;
}

@media (--desktop) {
  /* Override the fixed 400px width on desktop */
  .product-config .modal-container {
    width: auto;
    max-width: 562px;
  }

  .unfinished-decoration__modal {
    justify-items: center;
    padding: 42px 40px 25px;
  }

  .unfinished-decoration__modal-title {
    font-size: 16px;
    line-height: 24px;
  }

  .unfinished-decoration__modal-options {
    grid-auto-flow: column;
    grid-template-columns: 1fr 1fr;
    padding: 0;
  }

  .unfinished-decoration__modal-cancel {
    margin-right: 10px;
  }
}

@media (--minDesktopContent) {
  .modal-container--center .product-config-components {
    max-height: calc(670px - 48px);
  }

  .product-config:not(.modal-product-configurator) .product-config-components {
    display: flex;
  }

  .product-config:not(.modal-product-configurator) .product-config__main {
    width: 400px;
    margin-right: 15px;
  }

  .product-config:not(.modal-product-configurator) .product-config__summary {
    width: 36%;
    align-self: flex-start;
    position: sticky;
    top: 0px;
  }

  .product-config:not(.modal-product-configurator):not(
      .modal-product-configurator--checkout
    )
    .product-config__summary {
    top: 75px;
  }
}
</style>
